import { AsYouType, Metadata } from 'libphonenumber-js';

export const getTagsFromVirtualField = (field) => {
    const { tags, group } = field;

    if (group === 'virtual_field' && tags) {
        return tags.map((tagArray) => tagArray.map((tag) => tag.id));
    }

    return null;
};

const nationalPrefixCache = new Map();
const TOO_LONG = 'TOO_LONG';

const toDigits = (value) => value.replace(/\D/g, '');

const toDigitsAndSpaces = (formatted) => formatted
    .replace(/[^\d ]/g, ' ')
    .replace(/ +/g, ' ')
    .trim();

const hasVisualFormatting = (formatted) => /[^\d+]/.test(formatted);

const getNationalPrefix = (countryCode) => {
    if (!nationalPrefixCache.has(countryCode)) {
        const metadata = new Metadata();

        metadata.selectNumberingPlan(countryCode);
        nationalPrefixCache.set(countryCode, metadata.numberingPlan?.nationalPrefix() || '');
    }

    return nationalPrefixCache.get(countryCode);
};

// Formats digits as the user types, dropping any trailing digits that would
// push the number past the country's maximum length.
const formatWithinMaxLength = (countryCode, digits) => {
    let accepted = digits;
    const formatter = new AsYouType(countryCode);
    let formatted = formatter.input(accepted);

    while (formatter.validateLength() === TOO_LONG && accepted.length > 0) {
        accepted = accepted.slice(0, -1);
        formatter.reset();
        formatted = formatter.input(accepted);
    }

    return { formatted, formatter };
};

const stripLeadingPrefix = (value, prefix) => (
    value.startsWith(prefix) ? value.slice(prefix.length).trimStart() : value
);

// A trunk prefix is the leading digit(s) (e.g. the UK's "0") written when
// dialing a number domestically but dropped from its E.164 form. Some countries
// only apply spacing once that prefix is present, so when the user omits it we
// re-run formatting with the prefix prepended to borrow that spacing, then strip
// it back out of the displayed value. Returns null when the prefix doesn't help.
const formatUsingTrunkPrefix = (countryCode, digits, trunkPrefix, plain) => {
    const prefixed = formatWithinMaxLength(countryCode, `${trunkPrefix}${digits}`);

    const addsSpacing = hasVisualFormatting(prefixed.formatted);
    const isSameNumber = prefixed.formatter.getNumberValue() === plain.formatter.getNumberValue();

    if (!addsSpacing || !isSameNumber) {
        return null;
    }

    return {
        formatted: stripLeadingPrefix(toDigitsAndSpaces(prefixed.formatted), trunkPrefix),
        e164: prefixed.formatter.getNumberValue(),
    };
};

export const formatPhoneNumber = (countryCode, phoneNumber) => {
    const digits = toDigits(phoneNumber);

    if (digits.length === 0) {
        return {
            formatted: '',
            e164: undefined,
        };
    }

    const plain = formatWithinMaxLength(countryCode, digits);
    const trunkPrefix = getNationalPrefix(countryCode);

    const shouldTryTrunkPrefix = trunkPrefix
        && !digits.startsWith(trunkPrefix)
        && !hasVisualFormatting(plain.formatted);

    const trunkPrefixResult = shouldTryTrunkPrefix
        ? formatUsingTrunkPrefix(countryCode, digits, trunkPrefix, plain)
        : null;

    if (trunkPrefixResult) {
        return trunkPrefixResult;
    }

    return {
        formatted: toDigitsAndSpaces(plain.formatted),
        e164: plain.formatter.getNumberValue(),
    };
};
